import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { Portal } from '~/Partials/Modal/Portal';
import type { ModalWrapperProps } from '~/Partials/ModalWrapper/ModalWrapper';
import { ModalWrapper } from '~/Partials/ModalWrapper/ModalWrapper';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { animationDurationMs } from '~/Utils/AnimationsUtils';
import { useIsMounted } from '~/Utils/Hooks/useIsMounted';
import styles from './Overlay.module.css';

export interface OverlayProps extends Pick<ModalWrapperProps, 'close'> {
    id: string;
    overlayLabel: string;
    isActive: boolean;
    closeLabel: string;
    className?: string;
}

const InvisibleDiv: React.FC<React.PropsWithChildren> = props => {
    const { children } = props;

    return <div style={{ display: 'none' }}>{children}</div>;
};

export const Overlay: React.FC<React.PropsWithChildren<OverlayProps>> = props => {
    const { id, overlayLabel, isActive, close, closeLabel, children, className } = props;

    const [isAnimationInProgress, setIsAnimationInProgress] = React.useState(false);
    const animatedElement = React.useRef<HTMLDivElement>(null);
    const backgroundRef = React.useRef<HTMLDivElement>(null);
    const isMounted = useIsMounted();

    const isOverlayVisible = isActive || isAnimationInProgress;

    // Must be set as an inline style because FocusTrap update handler launches before conditional class styles
    // are applied to the element. This handler searches for tabbable element and checks if it's visible
    // (i.e. neither it nor its parents have display:none) - if not it will throw an error.
    const displayMode = isOverlayVisible ? undefined : 'none';

    const labelId = uniqueId('overlayLabel_');

    // In order to support SSR (so that the content is indexed), we cannot use the Portal component
    // which is solely client-side. Instead, we use a dummy div that is hidden from the user and after
    // the component is mounted, we replace it with the Portal.
    const WrapperElement = isMounted ? Portal : InvisibleDiv;

    return (
        <WrapperElement>
            <div
                className={classNames(styles.Overlay, className)}
                style={{ display: displayMode }}
                id={id}
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelId}
            >
                <CSSTransition nodeRef={backgroundRef} in={isActive} appear timeout={animationDurationMs}>
                    <div ref={backgroundRef} className={styles.Background} />
                </CSSTransition>
                <CSSTransition
                    nodeRef={animatedElement}
                    in={isActive}
                    appear
                    timeout={animationDurationMs}
                    onEnter={() => setIsAnimationInProgress(true)}
                    onExited={() => setIsAnimationInProgress(false)}
                >
                    <ModalWrapper
                        close={close}
                        isFocusTrapActive={isActive}
                        isScrollLocked={isOverlayVisible}
                        shouldHideMainScrollBar
                    >
                        {buttonClickClose => (
                            <div ref={animatedElement} className={styles.Inner}>
                                <div className={styles.TopBanner}>
                                    <Button
                                        onClick={buttonClickClose}
                                        className={styles.CloseButton}
                                        type={ButtonType.Clear}
                                        title={closeLabel}
                                    >
                                        <SvgIcon iconName="circle-close" />
                                        <span className="visuallyHidden">{closeLabel}</span>
                                    </Button>
                                </div>
                                <div className={styles.Content}>
                                    <span id={labelId} className="visuallyHidden">
                                        {overlayLabel}
                                    </span>
                                    {children}
                                </div>
                            </div>
                        )}
                    </ModalWrapper>
                </CSSTransition>
            </div>
        </WrapperElement>
    );
};
